import { Controller } from "@hotwired/stimulus"
import { saveToCookie, loadFromCookie } from "../helpers/cookie_storage"

const SPEND_LABEL = "Spend"

export default class extends Controller {
  static targets = ["chart", "legendItem"]
  static values = { chartId: String }

  connect() {
    this.chartController = null
    this.initializeChartConnection()
  }

  initializeChartConnection() {
    // Wait for the chart controller to be initialized
    const checkChart = setInterval(() => {
      const chartElement = this.chartTarget
      if (chartElement && chartElement.dataset.iqrChartConnected) {
        this.chartController = this.application.getControllerForElementAndIdentifier(
          chartElement,
          "iqr-chart"
        )
        clearInterval(checkChart)

        // Restore saved state from cookie
        this.restoreStateFromCookie()
      }
    }, 100)
  }

  getCookieName() {
    if (!this.chartController) return null

    // Prefer account_id + platform for a semantic key; fall back to chart element id
    if (this.chartController.accountId && this.chartController.platform) {
      const adAccountPart = this.chartController.adAccountId ? `_${this.chartController.adAccountId}` : ""
      return `iqr_chart_state_${this.chartController.accountId}_${this.chartController.platform}${adAccountPart}`
    }

    // Fall back to chart element id (always unique per page, set via chart_id:)
    const chartId = this.chartTarget?.id
    return chartId ? `iqr_chart_state_${chartId}` : null
  }

  saveStateToCookie() {
    const cookieName = this.getCookieName()
    if (!cookieName) return

    const chart = this.getChart()
    if (!chart) return

    // Get the hidden state of all original datasets (skip Spend — always visible)
    const originalCount = this.chartController?.originalBarDatasetCount || 0
    const hiddenStates = {}

    for (let i = 0; i < originalCount; i++) {
      const dataset = chart.data.datasets[i]
      if (dataset.label === SPEND_LABEL) continue
      hiddenStates[dataset.label] = dataset.hidden || false
    }

    saveToCookie(cookieName, hiddenStates, 30)
  }

  restoreStateFromCookie() {
    const cookieName = this.getCookieName()
    if (!cookieName) return

    const hiddenStates = loadFromCookie(cookieName)
    if (!hiddenStates) return

    const chart = this.getChart()
    if (!chart) return

    // Apply saved states (never hide Spend — self-heals affected accounts)
    const originalCount = this.chartController?.originalBarDatasetCount || 0
    for (let i = 0; i < originalCount; i++) {
      const dataset = chart.data.datasets[i]
      if (dataset.label === SPEND_LABEL) continue
      if (Object.prototype.hasOwnProperty.call(hiddenStates, dataset.label)) {
        dataset.hidden = hiddenStates[dataset.label]

        // Update corresponding checkbox
        const checkbox = this.element.querySelector(`input[data-dataset-index="${i}"]`)
        if (checkbox) {
          checkbox.checked = !dataset.hidden
        }

        this.syncExternalCheckboxes(i, !dataset.hidden)
      }
    }

    // Update chart and axis visibility
    chart.update()
    this.updateAxisVisibility()

    // Update stored hidden states in chart controller
    if (this.chartController && this.chartController.updateStoredHiddenStates) {
      this.chartController.updateStoredHiddenStates()
    }
  }

  toggleDataset(event) {
    const checkbox = event.target
    const datasetIndex = parseInt(checkbox.dataset.datasetIndex)
    const chart = this.getChart()

    if (chart) {
      chart.data.datasets[datasetIndex].hidden = !checkbox.checked
      chart.update()

      // Update axis visibility based on visible datasets
      this.updateAxisVisibility()

      // Save state to cookie
      this.saveStateToCookie()

      // Notify the chart controller to update its stored hidden states
      if (this.chartController && this.chartController.updateStoredHiddenStates) {
        this.chartController.updateStoredHiddenStates()
      }

      this.syncExternalCheckboxes(datasetIndex, checkbox.checked)
    }
  }

  setDatasetVisibility(datasetIndex, visible) {
    const chart = this.getChart()
    if (!chart) return

    const dataset = chart.data.datasets[datasetIndex]
    if (!dataset) return

    dataset.hidden = !visible
    chart.update()
    this.updateAxisVisibility()
    this.saveStateToCookie()

    if (this.chartController && this.chartController.updateStoredHiddenStates) {
      this.chartController.updateStoredHiddenStates()
    }

    this.updateLegendCheckbox(datasetIndex, visible)
    this.syncExternalCheckboxes(datasetIndex, visible)
  }

  updateLegendCheckbox(datasetIndex, visible) {
    const checkbox = this.element.querySelector(`input[data-dataset-index="${datasetIndex}"]`)
    if (checkbox) {
      checkbox.checked = visible
    }
  }

  syncExternalCheckboxes(datasetIndex, visible) {
    if (!this.chartIdValue) return

    const selector = `[data-controller~="conversion-series-toggle"][data-conversion-series-toggle-chart-id-value="${this.chartIdValue}"][data-conversion-series-toggle-dataset-index-value="${datasetIndex}"] input[type="checkbox"]`
    document.querySelectorAll(selector).forEach((checkbox) => {
      checkbox.checked = visible
    })
  }

  showAll() {
    const chart = this.getChart()
    if (!chart) return

    // Show all original datasets except Spend (already always visible)
    const originalCount = this.chartController?.originalBarDatasetCount || 0
    for (let i = 0; i < originalCount; i++) {
      if (chart.data.datasets[i].label === SPEND_LABEL) continue
      chart.data.datasets[i].hidden = false
      this.syncExternalCheckboxes(i, true)
    }
    chart.update()

    // Update checkboxes (skip disabled ones, i.e. Spend)
    this.element.querySelectorAll('input[type="checkbox"]:not(:disabled)').forEach(cb => {
      cb.checked = true
    })

    // Update axis visibility
    this.updateAxisVisibility()

    // Save state to cookie
    this.saveStateToCookie()

    // Notify the chart controller to update its stored hidden states
    if (this.chartController && this.chartController.updateStoredHiddenStates) {
      this.chartController.updateStoredHiddenStates()
    }
  }

  hideAll() {
    const chart = this.getChart()
    if (!chart) return

    // Hide all original datasets except Spend
    const originalCount = this.chartController?.originalBarDatasetCount || 0
    for (let i = 0; i < originalCount; i++) {
      if (chart.data.datasets[i].label === SPEND_LABEL) continue
      chart.data.datasets[i].hidden = true
      this.syncExternalCheckboxes(i, false)
    }
    chart.update()

    // Update checkboxes (skip disabled ones, i.e. Spend)
    this.element.querySelectorAll('input[type="checkbox"]:not(:disabled)').forEach(cb => {
      cb.checked = false
    })

    // Update axis visibility
    this.updateAxisVisibility()

    // Save state to cookie
    this.saveStateToCookie()

    // Notify the chart controller to update its stored hidden states
    if (this.chartController && this.chartController.updateStoredHiddenStates) {
      this.chartController.updateStoredHiddenStates()
    }
  }

  showIqr(event) {
    const datasetIndex = parseInt(event.currentTarget.dataset.datasetIndex)
    if (this.chartController && this.chartController.debouncedHoverHandler) {
      // Simulate hover event on the legend
      const fakeEvent = { type: 'mouseover' }
      const fakeItem = { datasetIndex }
      const fakeLegend = { chart: this.getChart() }
      this.chartController.debouncedHoverHandler(fakeEvent, fakeItem, fakeLegend)
    }
  }

  hideIqr(event) {
    if (this.chartController && this.chartController.debouncedLeaveHandler) {
      // Simulate leave event on the legend
      const fakeEvent = { type: 'mouseleave' }
      const fakeItem = { datasetIndex: 0 }
      const fakeLegend = { chart: this.getChart() }
      this.chartController.debouncedLeaveHandler(fakeEvent, fakeItem, fakeLegend)
    }
  }

  getChart() {
    return this.chartController?.chartInstance
  }

  updateAxisVisibility() {
    const chart = this.getChart()
    if (!chart) return

    // Check if any conversion datasets are visible
    let hasVisibleConversions = false
    let hasVisibleSpend = false

    const originalCount = this.chartController?.originalBarDatasetCount || 0
    for (let i = 0; i < originalCount; i++) {
      const dataset = chart.data.datasets[i]
      if (!dataset.hidden) {
        if (dataset.label === SPEND_LABEL) {
          hasVisibleSpend = true
        } else {
          hasVisibleConversions = true
        }
      }
    }

    // Update axis visibility
    if (chart.options.scales.yConversions) {
      chart.options.scales.yConversions.display = hasVisibleConversions
    }
    if (chart.options.scales.ySpend) {
      chart.options.scales.ySpend.display = hasVisibleSpend
    }

    chart.update()
  }
}
